import { Injectable } from '@angular/core';
import { equalTo, orderByChild } from '@angular/fire/database';
import { DatabaseService } from './database.service';
import { HelperService } from './helper.service';

export type userStatTypes =
  | "weight"
  | "sleep"
  | "cycle"
  | "steps"
  | "calories"
  | "totalCalories"
  | "caloricIntake"
  | "bodyFat"
  | "distance"
  | "flightsClimbed"
  | "restingCalories"
  | "activeMinutes"
  | "restingHeartRate"
  | "heartRate"
  | "hrv"
  | "vo2Max"
  | "bpSystolic"
  | "bpDiastolic"
  | "spo2"
  | "respiratoryRate"
  | "height"
  | "water"
  | "bloodGlucose"
  | "bodyTemperature"
  | "basalBodyTemperature"
  | "protein"
  | "fat"
  | "carbs"
  | "fiber"
  | "sugar"
  | "sodium"
  | "lightSleep"
  | "deepSleep"
  | "remSleep"
  | "overnightHr"
  | "overnightHrv";

@Injectable({
  providedIn: 'root'
})
export class FitnessService {
    private setTypes = {
        straightSet: {
            name: 'Straight Set',
            value: 'straightSet',
            length: 1,
        },
        superSet: {
            name: 'Super Set',
            value: 'superSet',
            length: 2,
        },
        triSet: {
            name: 'Tri Set',
            value: 'triSet',
            length: 3,
        },
        singleDropSet: {
            name: 'Single Drop Set',
            value: 'singleDropSet',
            length: 1,
            dropSets: 1
        },
        dropSet: {
            name: 'Double Drop Set',
            value: 'dropSet',
            length: 1,
            dropSets: 2
        },
        tripleDropSet: {
            name: 'Triple Drop Set',
            value: 'tripleDropSet',
            length: 1,
            dropSets: 3,
        },
        halfRep: {
            name: '1 1/2 Rep',
            value: 'halfRep',
            length: 1,
        },
        pauseSet: {
            name: 'Pause Set',
            value: 'pauseSet',
            length: 1,
        },
        giantSet: {
            name: 'Giant Set',
            value: 'giantSet',
            disabled: true,
            length: 1,
        },
        giantSetNew: {
            name: 'Giant Set',
            value: 'giantSetNew',
            length: 4,
        },
        incrementalHalfReps: {
            name: 'Incremental Half Reps',
            value: 'incrementalHalfReps',
            disabled: true,
            length: 1,
        },
        tripleClusterSet: {
            name: 'Triple Cluster Set',
            value: 'tripleClusterSet',
            length: 1,
        },
        amrapSet: {
            name: 'AMRAP Set',
            value: 'amrapSet',
            length: 1,
        },
        pyramidSet: {
            name: 'Pyramid Set',
            value: 'pyramidSet',
            length: 1,
        },
        pulseSet: {
            name: 'Pulse Set',
            value: 'pulseSet',
            length: 1,
        },
        circuit: {
            name: 'Circuit',
            value: 'circuit',
            dynamic: "true",
            length: null,
        }
    }
    constructor(
        private dbService: DatabaseService,
        private helperService: HelperService,
    ) { }

    getSetType(value: string) {
        return this.setTypes[value]
    }

    getSetTypes() {
        return Object.assign({}, this.setTypes)
    }

    getMeasurementUnits() {
        return {
            calories: {
                name: "Calories",
                abbreviation: "Cals"
            },
            distance: {
                name: "Distance (km)",
                inactive: true,
                abbreviation: "Km"
            },
            feet: {
                name: "Feet",
                abbreviation: "Ft",
                tracking: true,
            },
            km: {
                name: "Km",
                abbreviation: "Km",
                tracking: true,
            },
            meters: {
                name: "Meters",
                abbreviation: "Meters",
                tracking: true,
            },
            miles: {
                name: "Miles",
                abbreviation: "Miles",
                tracking: true
            },
            minutes: {
                name: "Minutes",
                abbreviation: "Min"
            },
            time: {
                name: "Time (min)",
                inactive: true,
                abbreviation: "Min"
            },
            steps: {
                name: "Steps",
                abbreviation: "Steps",
                tracking: true
            },
        }
    }

    calculateBMR(userDetails) {
      let gender = userDetails.gender == 'male'? 1 : 0;
      return Math.round((6.176 * userDetails.weight * ((100 - userDetails.bodyfat) / 100)) + (4.369 * userDetails.weight * (userDetails.bodyfat / 100))+(198 * gender)-(3.351 * userDetails.age) + 674);
    }

    calculateBMRMSJE(userDetails, unit: "metric" | "imperial" = "imperial") {
        const weight = unit === "metric"? this.helperService.kgToLbs(userDetails.weight) : userDetails.weight;
        const height = unit === "metric"? this.helperService.cmToInches(userDetails.height) : userDetails.height;
        if(userDetails.gender === "male") {
            return 10 * weight *  0.45359237  + 6.25 * height * 2.54 - 5 * userDetails.age + 5
        } else {
            return 10 * weight *  0.45359237  + 6.25 * height * 2.54 - 5 * userDetails.age - 161
        }
    }

    /** BMI from weight + height. Imperial: lbs + inches; metric: kg + cm. */
    calculateBMI(
      weight: number,
      height: number,
      unit: "metric" | "imperial" = "imperial",
    ): number | null {
      if (!Number.isFinite(weight) || !Number.isFinite(height) || weight <= 0 || height <= 0) {
        return null;
      }
      if (unit === "metric") {
        const meters = height / 100;
        if (meters <= 0) return null;
        return Math.round((weight / (meters * meters)) * 10) / 10;
      }
      return Math.round(((703 * weight) / (height * height)) * 10) / 10;
    }

    calculateMacros(userDetails, caloricAdjustment, proteinFactor, fatsFactor) {
      let bmr = this.calculateBMR(userDetails);
      let macros: any = {};
      macros.calories = Math.round(userDetails.activity * bmr + caloricAdjustment);
      macros.protein = Math.round(userDetails.weight * ((100 - userDetails.bodyfat) / 100) * proteinFactor);
      macros.fats = Math.round(macros.calories * fatsFactor / 9);
      macros.carbs = Math.round(((macros.calories - (macros.protein * 4) - (macros.fats * 9))) / 4);
      return macros
    }

    calculateMacrosET(userDetails, caloricAdjustment, protein, fats, carbs) {
        let bmr = this.calculateBMR(userDetails);
        let macros: any = {};
        macros.calories = Math.round(userDetails.activity * bmr * userDetails.bodyType + caloricAdjustment);
        macros.protein = Math.round(macros.calories * protein / 100 / 4);
        macros.fats = Math.round(macros.calories * fats / 100 / 9);
        macros.carbs = Math.round(macros.calories * carbs / 100 / 4);
        return macros
      }

    calculateCalories(protein: number, carbs: number, fats: number) {
      let calories = 0;
      if(protein !== null && protein !== undefined) {
        calories += protein * 4;
      }
      if(carbs !== null && carbs !== undefined) {
        calories += carbs * 4;
      }
      if(fats !== null && fats !== undefined) {
        calories += fats * 9;
      }
      return calories
    }

    calculateBodyfat7Pinch(age: number, bodyfatMeasurements) {
		let bodyfatMeasurementsSum = bodyfatMeasurements.chest + bodyfatMeasurements.triceps + bodyfatMeasurements.subscapular + bodyfatMeasurements.midaxilary + bodyfatMeasurements.suprailiac + bodyfatMeasurements.umbilical + bodyfatMeasurements.quads;
	    return +(495 / (1.097 - (0.00046971) * (bodyfatMeasurementsSum) + 0.00000056 * (bodyfatMeasurementsSum) * (bodyfatMeasurementsSum) - (0.00012828 * age)) - 450).toFixed(1);
	}

    calculateCardioMinutes(cardio) {
        if(cardio?.sessions) {
            let toReturn = 0;
            for (const session of cardio.sessions) {
                if(session.tracking && session.tracking.time && !isNaN(session.tracking.time)) {
                    toReturn += session.tracking.time;
                }
            }
            return toReturn;
        } else return 0;
    }

    calculateCardioCalories(cardio) {
        if(cardio?.sessions) {
            let toReturn = 0;
            for (const session of cardio.sessions) {
                if(session.tracking && session.tracking.calories && !isNaN(session.tracking.calories)) {
                    toReturn += session.tracking.calories;
                }
            }
            return toReturn;
        } else return 0;
    }

    calculateCardioSteps(cardio) {
        if(cardio?.sessions) {
            let toReturn = 0;
            for (const session of cardio.sessions) {
                if(session.tracking && session.tracking.measurementType === "steps" && session.tracking.amount != null && session.tracking.amount !== '') {
                    const raw = session.tracking.amount;
                    const amount = typeof raw === 'number'
                      ? raw
                      : Number(String(raw).replace(/\s+/g, ''));
                    if (!isNaN(amount)) {
                      toReturn += amount;
                    }
                }
            }
            return toReturn;
        } else return 0;
    }

    /**
     * Sum cardio session tracking.amount for distance units, converted to miles.
     * Skips steps / time / calories measurement types.
     */
    calculateCardioDistanceMiles(cardio): number {
        if (!cardio?.sessions) return 0;
        let toReturn = 0;
        for (const session of cardio.sessions) {
            const miles = this.cardioTrackingAmountToMiles(session?.tracking);
            if (miles != null && miles > 0) {
                toReturn += miles;
            }
        }
        return toReturn;
    }

    /** Convert a cardio tracking amount+unit to miles, or null if not a distance unit. */
    cardioTrackingAmountToMiles(tracking: {
        amount?: string | number | null;
        measurementType?: string | null;
    } | null | undefined): number | null {
        if (!tracking) return null;
        const unit = String(tracking.measurementType || '').toLowerCase();
        const factor = this.distanceUnitToMilesFactor(unit);
        if (factor == null) return null;
        const raw = tracking.amount;
        if (raw == null || raw === '') return null;
        const amount =
            typeof raw === 'number' ? raw : Number(String(raw).replace(/\s+/g, ''));
        if (!Number.isFinite(amount) || amount <= 0) return null;
        return amount * factor;
    }

    /** Miles multiplier for distance measurementType keys used in cardio tracking. */
    distanceUnitToMilesFactor(unit: string): number | null {
        switch (String(unit || '').toLowerCase()) {
            case 'miles':
            case 'mi':
                return 1;
            case 'km':
            case 'distance': // legacy alias = km
                return 0.621371;
            case 'meters':
            case 'm':
                return 0.000621371;
            case 'feet':
            case 'ft':
                return 1 / 5280;
            default:
                return null;
        }
    }

    /**
     * Session length in whole minutes from startD → endD/trackingCompleted.
     * Falls back to creationDate when completed workouts never wrote startD.
     */
    workoutSessionDurationMinutes(workout: {
        startD?: string | number | null;
        endD?: string | number | null;
        creationDate?: string | number | null;
        trackingCompleted?: string | number | null;
    } | null | undefined): number | null {
        if (!workout?.trackingCompleted) return null;
        const startRaw =
            workout.startD != null && workout.startD !== ''
                ? workout.startD
                : workout.creationDate;
        const startMs = this.parseWorkoutTimeMs(startRaw);
        const endMs = this.parseWorkoutTimeMs(workout.endD ?? workout.trackingCompleted);
        if (startMs == null || endMs == null) return null;
        const mins = Math.floor(Math.max(0, endMs - startMs) / 60000);
        return Number.isFinite(mins) ? mins : null;
    }

    private parseWorkoutTimeMs(value: string | number | null | undefined): number | null {
        if (value == null || value === '') return null;
        if (typeof value === 'number') {
            return value < 1e12 ? value * 1000 : value;
        }
        const ms = new Date(value).getTime();
        return Number.isNaN(ms) ? null : ms;
    }

    calculateWorkoutVolume(workout) {
        if(workout?.sets) {
            let toReturn = 0;
            for (const set of workout.sets) {
                if(!isNaN(set.sets)) {
                    const multiplier = set.sets;
                    let factor = 1;
                    if(set.type) {
                        const setType = this.getSetType(set.type);
                        if(setType && typeof setType.length == 'number') {
                            factor = setType.length;
                        } else {
                            if(set.exercises?.length > 0) {
                                factor = set.exercises.length;
                            }
                        }
                    }
                    toReturn +=  multiplier * factor;
                }
            }
            return toReturn;
        } else return 0;
    }

    async getUserStatByType(userId: string, stat: userStatTypes): Promise<any> {
        const snap = await this.dbService.getSnapshot(`userStats/${userId}/${stat}`);
        return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
    }

    async getUserStat(userId: string, stat: userStatTypes, statId: string): Promise<any> {
        const snap = await this.dbService.getSnapshot(`userStats/${userId}/${stat}/${statId}`);
        return snap? {...snap, id: statId} : null;
    }

    async getUserStatByDate(userId: string, stat: userStatTypes, date: string): Promise<any> {
      const snap = await this.dbService.getSnapshot(
        `userStats/${userId}/${stat}`,
        orderByChild('date'),
        equalTo(date),
      );
      return snap ? this.helperService.transformFirebaseObjectToArray(snap)[0] : null;
    }

    addUserStat(userId: string, stat: userStatTypes, data: any) {
      const toSave = this.helperService.cleanObjectForFirebase({
        ...data,
        creationDate: new Date().toISOString(),
      });
      return this.dbService.pushByRef(`userStats/${userId}/${stat}`, toSave);
    }

    updateUserStat(userId: string, stat: userStatTypes, statId: string, data: any) {
      const toSave = this.helperService.cleanObjectForFirebase({
        ...data,
        lastModifiedDate: new Date().toISOString(),
      });
      return this.dbService.updateRef(`userStats/${userId}/${stat}/${statId}`, toSave);
    }

    /** Create or update the single stat record for a given date. */
    async upsertUserStatByDate(userId: string, stat: userStatTypes, data: any) {
      const existing = await this.getUserStatByDate(userId, stat, data.date);
      if (existing?.id) {
        await this.updateUserStat(userId, stat, existing.id, data);
        return existing.id;
      }
      const created = await this.addUserStat(userId, stat, data);
      return (created as any)?.key ?? null;
    }

    /**
     * Prepare a tracked set for a new tracking session (e.g. "track from previous"):
     * clear load/reps but preserve structure including drop sets count.
     */
    private cleanTrackedSetForNewSession(previous?: any): any {
        const base: any = {
            reps: null,
            repType: previous?.repType || 'reps',
            weight: null,
            weightType: previous?.weightType || 'lbs',
            restLength: previous?.restLength || 'sec',
        };
        const prevDrops = previous?.dropSets;
        if (Array.isArray(prevDrops) && prevDrops.length > 0) {
            base.dropSets = prevDrops.map(() => ({
                weightType: 'lbs',
                reps: '',
                repType: 'reps',
                weight: '',
                restLength: 'sec',
            }));
        }
        return base;
    }

    cleanWorkoutTracking(tracking) {
        if(tracking) {
            const toReturn = this.helperService.copyObject(tracking);
            for (let set of toReturn) {
                // Protection for empty exercises - TO BE REMOVED
                set.exercises = set.exercises || [];
                for (let exercise of set.exercises) {
                delete exercise.videoUrl;
                if (!exercise.trackedSets) {
                    exercise.trackedSets = [];
                    for (let i = 0; i < set.sets; i++) {
                    exercise.trackedSets.push(this.cleanTrackedSetForNewSession(undefined));
                    }
                } else {
                    for (let i = 0; i < set.sets; i++) {
                    exercise.trackedSets[i] = this.cleanTrackedSetForNewSession(exercise.trackedSets[i]);
                    }
                }
                }
            }
            return toReturn;
            } else {
            return null
            }
    }

    cleanCardioTracking(tracking) {
        if(tracking) {
            const toReturn = this.helperService.copyObject(tracking);
            for (let set of toReturn) {
              set.tracking = set.tracking || {};
            }
            return toReturn;
          } else {
            return null
        }
    }
}
